package com.patronat.top.ui.molecules.inputs

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsFocusedAsState
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.graphics.takeOrElse
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import com.patronat.top.ui.atoms.inputs.InputLabel
import com.patronat.top.ui.atoms.inputs.InputState
import com.patronat.top.ui.atoms.inputs.InputStyles
import com.patronat.top.ui.theme.AccentPink
import com.patronat.top.ui.theme.InputLabelText
import com.patronat.top.ui.theme.InputPlaceholderText
import com.patronat.top.ui.theme.InputTextContent
import com.patronat.top.ui.theme.MineShaft100
import com.patronat.top.ui.theme.MineShaft200
import com.patronat.top.ui.theme.MineShaft500
import com.patronat.top.ui.theme.MineShaft600
import com.patronat.top.ui.theme.MineShaft900
import com.patronat.top.ui.theme.Primary100
import com.patronat.top.ui.theme.Primary400
import com.patronat.top.ui.theme.Primary600
import com.patronat.top.ui.theme.White

@Composable
fun AppTextArea(
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    label: String? = null,
    labelTextStyle: TextStyle = InputLabelText,
    contentTextStyle: TextStyle = InputTextContent,
    placeholder: String? = null,
    forceState: InputState? = null,
    enabled: Boolean = true,
    readOnly: Boolean = false,
    minHeight: Dp = 120.dp,
    maxLines: Int = 6,
    keyboardOptions: KeyboardOptions = KeyboardOptions.Default,
    keyboardActions: KeyboardActions = KeyboardActions.Default
) {
    val interactionSource = remember { MutableInteractionSource() }
    val isFocused by interactionSource.collectIsFocusedAsState()

    val currentState = when {
        forceState != null -> forceState
        !enabled -> InputState.Disabled
        readOnly -> InputState.ReadOnly
        isFocused -> InputState.Active
        value.isNotEmpty() -> InputState.Filled
        else -> InputState.Default
    }
    val styles = getTextAreaStyles(
        state = currentState,
        contentTextStyle = contentTextStyle
    )

    Column(modifier = modifier.fillMaxWidth()) {
        if (label != null) {
            InputLabel(
                text = label,
                textStyle = labelTextStyle
            )
            Spacer(modifier = Modifier.height(8.dp))
        }

        BasicTextField(
            value = value,
            onValueChange = onValueChange,
            enabled = enabled,
            readOnly = readOnly,
            textStyle = styles.textColor,
            keyboardOptions = keyboardOptions,
            keyboardActions = keyboardActions,
            interactionSource = interactionSource,
            singleLine = false,
            maxLines = maxLines,
            cursorBrush = SolidColor(Primary600),
            modifier = Modifier.fillMaxWidth(),
            decorationBox = { innerTextField ->
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .heightIn(min = minHeight)
                        .background(styles.backgroundColor, RoundedCornerShape(8.dp))
                        .border(styles.borderWidth, styles.borderColor, RoundedCornerShape(8.dp))
                        .padding(16.dp),
                    contentAlignment = Alignment.TopStart
                ) {
                    if (value.isEmpty() && placeholder != null) {
                        Text(
                            text = placeholder,
                            style = InputPlaceholderText,
                            color = InputPlaceholderText.color.takeOrElse { styles.placeholderColor }
                        )
                    }
                    innerTextField()
                }
            }
        )
    }
}

private fun getTextAreaStyles(
    state: InputState,
    contentTextStyle: TextStyle
): InputStyles {
    val baseTextStyle = contentTextStyle

    return when (state) {
        InputState.Default -> InputStyles(
            borderColor = MineShaft200,
            borderWidth = 0.5.dp,
            backgroundColor = White,
            textColor = baseTextStyle.copy(color = MineShaft500),
            placeholderColor = MineShaft500,
            iconColor = Primary600
        )
        InputState.Active -> InputStyles(
            borderColor = Primary600,
            borderWidth = 1.dp,
            backgroundColor = Primary100,
            textColor = baseTextStyle.copy(color = MineShaft900),
            placeholderColor = MineShaft500,
            iconColor = Primary600
        )
        InputState.Filled -> InputStyles(
            borderColor = MineShaft200,
            borderWidth = 1.dp,
            backgroundColor = White,
            textColor = baseTextStyle.copy(color = MineShaft900),
            placeholderColor = MineShaft500,
            iconColor = MineShaft900
        )
        InputState.ReadOnly -> InputStyles(
            borderColor = MineShaft200,
            borderWidth = 1.dp,
            backgroundColor = White,
            textColor = baseTextStyle.copy(color = MineShaft600),
            placeholderColor = MineShaft600,
            iconColor = MineShaft900
        )
        InputState.Error -> InputStyles(
            borderColor = AccentPink,
            borderWidth = 1.dp,
            backgroundColor = White,
            textColor = baseTextStyle.copy(color = MineShaft900),
            placeholderColor = MineShaft500,
            iconColor = AccentPink
        )
        InputState.Success -> InputStyles(
            borderColor = Primary400,
            borderWidth = 1.dp,
            backgroundColor = White,
            textColor = baseTextStyle.copy(color = MineShaft900),
            placeholderColor = MineShaft500,
            iconColor = Primary600
        )
        InputState.Disabled -> InputStyles(
            borderColor = MineShaft100,
            borderWidth = 0.5.dp,
            backgroundColor = MineShaft100,
            textColor = baseTextStyle.copy(color = MineShaft600),
            placeholderColor = MineShaft600,
            iconColor = Primary600
        )
    }
}
